import React, { useCallback, useEffect, useState } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { token } from '@atlaskit/tokens';

import { MorphingShapeLoader } from '@atlassian/rovo-platform-ui-components/primitives/MorphingShapeLoader';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';
import { EllipsesAnimation } from '@atlassian/rovo-platform-ui-components/primitives/EllipsesAnimation';
import { ShimmerText } from '@atlassian/rovo-platform-ui-components/primitives/ShimmerText';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		height: '100%',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	loadingContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		width: '80%',
	},
});

const ROTATION_INTERVAL_MS = 8000;

const i18n = defineMessages({
	loadingTextReviewing: {
		id: 'suggestions-panel.loading-state.loading-text.reviewing',
		defaultMessage: 'Rovo is reviewing your page',
		description:
			'First rotating loading message shown in the suggestions panel while Rovo is generating suggestions',
	},
	loadingTextMayTakeMinute: {
		id: 'suggestions-panel.loading-state.loading-text.may-take-minute',
		defaultMessage: 'This may take a minute',
		description:
			'Second rotating loading message shown in the suggestions panel while Rovo is generating suggestions',
	},
	cancelButton: {
		id: 'suggestions-panel.loading-state.cancel-button',
		defaultMessage: 'Stop suggesting',
		description:
			'Button label to cancel/stop Rovo from generating suggestions in the suggestions panel',
	},
});

type SuggestionsLoadingStateProps = {
	api?: FullPageEditorPresetAPI;
};

export const SuggestionsLoadingState = ({
	api,
}: SuggestionsLoadingStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const loadingMessages = [
		formatMessage(i18n.loadingTextReviewing),
		formatMessage(i18n.loadingTextMayTakeMinute),
	];

	const [messageIndex, setMessageIndex] = useState(0);

	useEffect(() => {
		const interval = setInterval(() => {
			setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
		}, ROTATION_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [loadingMessages.length]);

	const handleCancel = useCallback(() => {
		api?.aiSuggestions?.actions?.stopThinking();
	}, [api]);

	return (
		<div css={styles.container} data-testid="suggestions-panel-loading-state">
			<div css={styles.loadingContainer}>
				<MorphingShapeLoader testId="suggestions-panel-thinking-indicator" size="large" />
				<ShimmerText size="medium">
					{loadingMessages[messageIndex]}
					<EllipsesAnimation isAnimated />
				</ShimmerText>
				<RainbowProgressBar
					testId="suggestions-panel-progress-bar"
					label={loadingMessages[messageIndex]}
				/>
			</div>
			<Button onClick={handleCancel}>{formatMessage(i18n.cancelButton)}</Button>
		</div>
	);
};
